import { CancelledError, VueQueryPlugin } from '@tanstack/vue-query';
import { FirebaseError } from 'firebase/app';
import { createPinia } from 'pinia';
import {
  type ComponentOptions,
  createApp as createNoSSRApp,
  createSSRApp,
} from 'vue';
import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
} from 'vue-router';
import ToastPlugin from 'vue-toast-notification';
import type { SSRContext } from 'vue/server-renderer';

import type { StripAtSuffixes } from '@scaffold/common';

import { type AppContext, queryClient, toast } from '@/context';
import { UnauthenticatedError } from '@/core/errors';
import {
  attachRouteLoading,
  createModuleGuard,
  createModuleRoutes,
  onRouterError,
  type RouteModule,
} from '@/core/routing';
import { buildMetaTags } from '@/core/utils';
import NotFound from '@/layouts/NotFound.vue';
import SinglePage from '@/layouts/SinglePage.vue';
import WIP from '@/layouts/WIP.vue';

import App from './App.vue';

export function isSilentError(err: unknown) {
  if (err instanceof FirebaseError && err.code === 'unavailable') {
    toast.error($t`Temporary offline. Try back later…`);

    return true;
  }

  if (err instanceof CancelledError) {
    // The route's query fetch was cancelled because this navigation was
    // already superseded (e.g. the user navigated away or the tab was
    // backgrounded while offline). Nothing to show for a navigation that
    // no longer applies.
    console.warn(err);

    return true;
  }

  if (
    err instanceof DOMException &&
    err.name === 'InvalidStateError' &&
    /database connection is closing/i.test(err.message)
  ) {
    // Firebase Auth's IndexedDB persistence can race with the browser closing the
    // connection (tab backgrounding, Safari eviction, etc). It's benign and
    // self-recovers, so it shouldn't blank the page like a real error.
    // https://github.com/firebase/firebase-js-sdk/issues/6786
    console.warn(err);

    return true;
  }

  return false;
}

function getMeta(locale: string) {
  const loaders = {
    'en-GB': import('@/meta.json?locale=en-GB'),
    'de-DE': import('@/meta.json?locale=de-DE'),
  };
  return (loaders[locale as keyof typeof loaders] || loaders['en-GB']).then(
    (mod) => mod.default as StripAtSuffixes<typeof import('@/meta.json')>,
  );
}

export async function createApp(
  ssrContext: SSRContext,
  noHydrate: boolean = false,
) {
  const locale = ssrContext.locale || 'en-GB';
  const meta = buildMetaTags(await getMeta(locale));

  const app = (noHydrate ? createNoSSRApp : createSSRApp)(App, { meta });

  if (!import.meta.env.SSR) app.use(ToastPlugin);

  app.use(VueQueryPlugin, {
    queryClient,
  });

  const pinia = createPinia();
  app.use(pinia);

  const routes =
    import.meta.env.VITE_WIP === 'true'
      ? [
          {
            path: '/:pathMatch(.*)*',
            alias: ['/404'],
            name: 'not-found',
            component: WIP,
            meta: import.meta.env.SSR ? { noHydrate: true } : undefined,
          },
        ]
      : createModuleRoutes(
          import.meta.glob<RouteModule>('@/pages/**/*.vue'),
          /\/src\/pages((?:\/[a-z_][^/]*)*?)(?:\/index)?\.vue$/,
        );

  if (import.meta.env.VITE_WIP !== 'true') {
    routes.push(
      {
        path: '/spa',
        name: 'spa',
        component: SinglePage,
        meta: import.meta.env.SSR ? { noHydrate: true } : undefined,
      },
      {
        path: '/:pathMatch(.*)*',
        alias: ['/404'],
        name: 'not-found',
        component: NotFound,
        meta: import.meta.env.SSR ? { noHydrate: true } : undefined,
      },
    );
  }

  const router = createRouter({
    history: import.meta.env.SSR
      ? createMemoryHistory(ssrContext.base)
      : createWebHistory(ssrContext.base),
    routes,
  });

  if (!import.meta.env.SSR) {
    attachRouteLoading(router);
  }

  router.beforeEach(
    createModuleGuard({
      context: { queryClient } as AppContext,
      router,
      routes,

      defaultLayout: () =>
        import('@/layouts/Default.vue') as Promise<{
          default: ComponentOptions;
        }>,

      onError: import.meta.env.SSR
        ? (_context, err, next) => {
            if (err instanceof Error) next(err);
            else throw err;
          }
        : (context, err, next) => {
            if (err instanceof UnauthenticatedError) {
              next({ name: 'auth', query: { path: context.to.fullPath } });
            } else if (isSilentError(err)) {
              next();
            } else {
              onRouterError(context, err, next);
            }
          },
    }),
  );

  app.use(router);

  return { app, routes, router };
}
